/**
 * KAN-743: чистые функции каталога.
 *
 * Дерево категорий строится из плоского списка по parent_id. product_count у
 * категории только свои товары, поэтому сумма по поддереву считается здесь.
 * Категория с parent_id вне списка (сирота) или в цикле становится корнем.
 */
import type { CatalogCategory } from '@/types/catalog';

export interface CategoryNode {
  id: string;
  name: string;
  parentId: string | null;
  depth: number;
  /** Свои товары категории. */
  productCount: number;
  /** Товары категории и всех потомков. */
  subtreeCount: number;
  subtreeAvailable: number;
  /** KAN-754: категория добавлена вручную. */
  manual: boolean;
  children: CategoryNode[];
}

const key = (id: string | number | null | undefined): string | null =>
  id === null || id === undefined ? null : String(id);

/** Родитель категории, если он есть в списке и не она сама. */
function parentMap(categories: readonly CatalogCategory[]): Map<string, string | null> {
  const ids = new Set(categories.map((c) => String(c.id)));
  const parents = new Map<string, string | null>();
  categories.forEach((c) => {
    const id = String(c.id);
    const parent = key(c.parent_id);
    parents.set(id, parent !== null && parent !== id && ids.has(parent) ? parent : null);
  });
  return parents;
}

/** Цепочка предков от ближнего к дальнему; на цикле обрывается. */
function walkUp(parents: Map<string, string | null>, id: string): string[] {
  const chain: string[] = [];
  const seen = new Set<string>([id]);
  let current = parents.get(id) ?? null;
  while (current !== null && !seen.has(current)) {
    chain.push(current);
    seen.add(current);
    current = parents.get(current) ?? null;
  }
  return chain;
}

/** Категория в цикле parent_id (дойдя вверх, возвращаемся к себе). */
function inCycle(parents: Map<string, string | null>, id: string): boolean {
  const seen = new Set<string>();
  let current = parents.get(id) ?? null;
  while (current !== null) {
    if (current === id) return true;
    if (seen.has(current)) return false;
    seen.add(current);
    current = parents.get(current) ?? null;
  }
  return false;
}

const byName = (a: CategoryNode, b: CategoryNode): number => a.name.localeCompare(b.name, 'ru');

export function buildCategoryTree(categories: readonly CatalogCategory[]): CategoryNode[] {
  const parents = parentMap(categories);
  // Цикл разрываем: категория цикла становится корнем, остальные цикла висят под ней.
  const cycleRoots = new Set<string>();
  categories.forEach((c) => {
    const id = String(c.id);
    if (!inCycle(parents, id)) return;
    const cycle = [id, ...walkUp(parents, id)];
    if (!cycle.some((member) => cycleRoots.has(member))) cycleRoots.add(id);
  });
  cycleRoots.forEach((id) => parents.set(id, null));

  const nodes = new Map<string, CategoryNode>();
  const available = new Map<string, number>();
  categories.forEach((c) => {
    const id = String(c.id);
    nodes.set(id, {
      id,
      name: c.name,
      parentId: parents.get(id) ?? null,
      depth: 0,
      productCount: c.product_count ?? 0,
      subtreeCount: 0,
      subtreeAvailable: 0,
      manual: c.manual === true,
      children: [],
    });
    available.set(id, c.available_count ?? 0);
  });

  const roots: CategoryNode[] = [];
  nodes.forEach((node) => {
    const parent = node.parentId === null ? undefined : nodes.get(node.parentId);
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  const finish = (node: CategoryNode, depth: number): void => {
    node.depth = depth;
    node.children.sort(byName);
    node.subtreeCount = node.productCount;
    node.subtreeAvailable = available.get(node.id) ?? 0;
    node.children.forEach((child) => {
      finish(child, depth + 1);
      node.subtreeCount += child.subtreeCount;
      node.subtreeAvailable += child.subtreeAvailable;
    });
  };
  roots.sort(byName);
  roots.forEach((root) => finish(root, 0));
  return roots;
}

export function findCategoryNode(tree: readonly CategoryNode[], id: string): CategoryNode | null {
  for (const node of tree) {
    if (node.id === id) return node;
    const found = findCategoryNode(node.children, id);
    if (found) return found;
  }
  return null;
}

/** Предки категории от корня до родителя (их раскрываем в дереве). */
export function ancestorIds(categories: readonly CatalogCategory[], id: string): string[] {
  const parents = parentMap(categories);
  if (!parents.has(id)) return [];
  return walkUp(parents, id).reverse();
}

const RUB_CODES = new Set(['RUB', 'RUR']);

/** 1234.5, RUB -> "1 234,5 ₽"; другая валюта кодом; null -> "Цена не указана". */
export function formatPrice(price: number | null | undefined, currency: string | null | undefined): string {
  if (price === null || price === undefined || Number.isNaN(Number(price))) return 'Цена не указана';
  const amount = Number(price).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const code = (currency ?? '').trim().toUpperCase();
  if (!code) return amount;
  return `${amount} ${RUB_CODES.has(code) ? '₽' : code}`;
}

/** Дата обновления фида "07.10.2026, 14:05" или "" без даты. */
export function formatFeedDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Поля фида ключ-значение для таблицы: вложенное JSON-ом, пустое пустой строкой. */
export function rawFieldEntries(raw: Record<string, unknown> | null | undefined): Array<[string, string]> {
  if (!raw) return [];
  return Object.entries(raw).map(([field, value]) => {
    if (value === null || value === undefined) return [field, ''];
    if (typeof value === 'string') return [field, value];
    if (typeof value === 'object') return [field, JSON.stringify(value)];
    return [field, String(value)];
  });
}

const HTML_TAG = /<\/?[a-z][^>]*>/i;

/**
 * Описание товара как обычный текст. HTML из фида не рендерится: блочные теги
 * и <br> становятся переносами, скрипты и стили выбрасываются, остальные теги
 * снимаются, сущности (&amp;) раскрываются. DOMParser не исполняет скрипты.
 */
export function descriptionToText(description: string | null | undefined): string {
  if (!description) return '';
  if (!HTML_TAG.test(description)) return description.trim();
  const marked = description
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n');
  const text = new DOMParser().parseFromString(marked, 'text/html').body.textContent ?? '';
  return text
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
